// Shared building blocks for logbook PDF generation.
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import "./fonts/Roboto-Regular-normal.js";
import "./fonts/Roboto-Bold-normal.js";

const clientLogo = require("@/assets/client_logo.png");

export function createLogbookDoc({ orientation = "landscape" } = {}) {
    return new jsPDF({ orientation });
}

// Draws the title text, client logo, and horizontal rule used at the top of the first page of every logbook PDF.
export function drawHeader(doc, { title, logoPath = clientLogo } = {}) {
    const pageWidth = doc.internal.pageSize.width;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(title, 10, 12);

    const imgWidth = 20;
    const imgHeight = 8;
    const imgX = pageWidth - imgWidth - 15;
    const imgY = 8;
    doc.addImage(logoPath, 'PNG', imgX, imgY, imgWidth, imgHeight);

    doc.line(10, 20, pageWidth - 10, 20);
}

// Stamps the "Author / Printed by / Page X of Y" footer onto every page of the document.
export function drawFooter(doc, { authorName }) {
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const totalPages = doc.internal.getNumberOfPages();

    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setDrawColor(0);
        doc.line(10, pageHeight - 12, pageWidth - 10, pageHeight - 12);
        doc.setFontSize(8);

        doc.text(`Author: ${authorName}`, 10, pageHeight - 6);

        const now = new Date();
        const formattedDate = `${now.getDate()}/${now.getMonth() + 1}/${now.getFullYear()}`;
        const text = `Printed by: ${authorName} on ${formattedDate}`;
        const textWidth = (doc.getStringUnitWidth(text) * 8) / 3;
        const x = (pageWidth - textWidth) / 2;

        doc.text(text, x, pageHeight - 6);
        doc.text(`Page ${i} of ${totalPages}`, pageWidth - 27, pageHeight - 6);
    }
}

export function buildFilename(logbookName) {
    const now = new Date();
    const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")
        }-${String(now.getDate()).padStart(2, "0")}_${String(now.getHours()).padStart(2, "0")
        }${String(now.getMinutes()).padStart(2, "0")}${String(now.getSeconds()).padStart(2, "0")}`;
    return `${logbookName}_${timestamp}.pdf`;
}

export function savePdf(doc, logbookName) {
    doc.save(buildFilename(logbookName));
}

// Style presets shared by the trainee/instructor/course info tables
export const INFO_TABLE_STYLE = {
    font: 'Roboto-Regular',
    fontSize: 10,
    lineWidth: 0.05,
    lineColor: [169, 169, 169],
};

export const INFO_TABLE_HEAD_STYLE = {
    fillColor: [41, 128, 185],
    textColor: [255, 255, 255],
    fontStyle: 'bold',
    font: 'Roboto-Bold',
};

// Groups a flat task list by chapt_no. Shared by every company layout that groups tasks per-chapter
export function groupTasksByChapter(tasks) {
    const grouped = {};
    for (const task of tasks) {
        if (task.chapt_no !== null && task.chapt_no !== undefined) {
            if (!grouped[task.chapt_no]) {
                grouped[task.chapt_no] = [];
            }
            grouped[task.chapt_no].push(task);
        }
    }
    return grouped;
}

// Natural-sorts chapter numbers like "9", "10", "10A" into 9, 10, 10A order
export function sortChapterNumbers(chaptNos) {
    return [...chaptNos].sort((a, b) => {
        const regex = /^(\d+)([A-Za-z]*)$/;
        const [, numA, suffixA] = (a || '').match(regex) || [];
        const [, numB, suffixB] = (b || '').match(regex) || [];

        const numberComparison = Number(numA) - Number(numB);
        if (numberComparison !== 0) return numberComparison;

        return (suffixA || '').localeCompare(suffixB || '');
    });
}

export function getChapterName(chapters, chapt_no) {
    const chapter = chapters.find((ch) => ch.chapt_no === chapt_no);
    return chapter ? `Chapter ${chapt_no}: ${chapter.name}` : `Chapter ${chapt_no}`;
}
